import { createEffect, createSignal, For, useContext } from 'solid-js';
import { useLocation, useNavigate, useSearchParams } from '@solidjs/router';
import { CourseCardData, CourseCard } from '../course-card/course-card';
import { Heading } from '../../ui-components/heading/heading';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { CourseTag } from '../course/course-types';
import { ShowpadDelayInfoBox } from '../showpad-delay-info-box/showpad-delay-info-box';
import { FormSelect, FormSelectWrapper, IconWrapper } from '../forms-tw/forms-fields/primitives';
import { ChevronDownCviIcon } from '../icons-library/chevron-down-cvi';

export type PathCategory = {
    categoryName: string;
    paths: PathWithCourses[];
};

type PathWithCourses = {
    id: string;
    name: string;
    curriculums: CurriculumsData[];
};

type CurriculumsData = {
    id: string;
    name: string;
    courses: PathCourse[];
};

type PathCourse = {
    courseId: string;
    name: string;
    description: string;
    progress: number;
    completedDate: string;
    learnMinutes: number;
    coverImageUrl: {
        small: string;
    };
    durationInSeconds: number;
    tags?: CourseTag[];
    path?: {
        id: string;
        name: string;
    };
    curriculum?: {
        id: string;
        name: string;
    };
}

type CoursePathProps = {
    pathsCategories: PathCategory[];
}

const CATEGORY_SELECT_ID = 'course-path-category-select';
const CATEGORY_SELECT_LABEL_ID = 'course-path-category-select-label';

export const CoursePath = (props: CoursePathProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { localize } = useContext(AppContext);

    const [showPathCategory, setShowPathCategory] = createSignal<string | null>();

    const paramSlugTranslation = () => localize('course-category-url-parameter', 'category');

    createEffect(() => {
        const categoryParam = searchParams[paramSlugTranslation()];

        if (categoryParam) {
            setShowPathCategory(categoryParam);
        } else {
            const firstCategory = props.pathsCategories?.[0]?.categoryName;
            setShowPathCategory(firstCategory);
        }
    });

    const selectCategory = (categoryName: string) => {
        setShowPathCategory(categoryName);
        navigate(location.pathname + `?${paramSlugTranslation()}=${categoryName}`, { scroll: false });
    };

    const PathComponent = ({ path }: { path: PathWithCourses }) => {
        return (
            <div id={path.name}>
                <div class="mt-16">
                    <For each={path.curriculums}>{(curriculum) => (
                        <CurriculumComponent curriculum={curriculum}/>
                    )}</For>
                </div>
            </div>
        );
    };

    const CurriculumComponent = ({ curriculum }: { curriculum: PathWithCourses['curriculums'][0] }) => {
        return (
            <section class="flex flex-col pt-1 pb-16 relative last:pb-0 last:mb-16 max-mobile:ml-0">
                <Heading tag='h3' variant='heading-lg' noBlockSpacing>
                    {curriculum.name}
                </Heading>

                <div class="[container-type:inline-size] mb-4 mt-12">
                    <div class="grid grid-rows-[auto] h-fit gap-[1.57rem] grid-cols-3 @max-[760px]:grid-cols-2 @max-[550px]:grid-cols-1">
                        <For each={curriculum.courses}>{(course: CourseCardData) => (
                            <CourseCard course={course} />
                        )}</For>
                    </div>
                </div>
            </section>
        );
    };

    /* Figma: a section heading above the DS formkit select (bordered native select + chevron).
       The h2 doubles as the select's label via aria-labelledby. */
    const PathCategorySelect = () => (
        <div class="flex w-full flex-col gap-cvi-6">
            <h2 id={CATEGORY_SELECT_LABEL_ID} class="m-0 heading-2xl font-cvi-medium text-cvi-fg-default">
                {localize('select-area-of-care-for-courses', 'Select area of care')}
            </h2>
            <div class="w-full max-w-cvi-form-column">
                <FormSelectWrapper>
                    <FormSelect
                        id={CATEGORY_SELECT_ID}
                        name="course-category"
                        value={showPathCategory() ?? ''}
                        onChange={(e) => selectCategory(e.currentTarget.value)}
                        aria-labelledby={CATEGORY_SELECT_LABEL_ID}
                        title={localize('select-area-of-care-for-courses', 'Select area of care')}
                    >
                        <For each={props.pathsCategories}>{(pathCategory: PathCategory) => (
                            <option value={pathCategory.categoryName}>{pathCategory.categoryName}</option>
                        )}</For>
                    </FormSelect>

                    <IconWrapper>
                        <ChevronDownCviIcon size="default" ariaHidden={true} />
                    </IconWrapper>
                </FormSelectWrapper>
            </div>
        </div>
    );

    return (
        <ErrorCatcher componentName='CoursePath'>
            <div class="flex flex-col justify-center my-8">
                <PathCategorySelect />
            </div>

            <For each={props.pathsCategories.filter(path => path.categoryName === showPathCategory())}>{(pathCategory) => (
                <For each={pathCategory.paths}>{(path) => (
                    <PathComponent path={path} />
                )}</For>
            )}</For>

            <ShowpadDelayInfoBox text={localize('showpad-progress-delay-info-text', 'Please note that it may take up to an hour for your progress to appear')}/>
        </ErrorCatcher>
    );
};
